import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  image,
  imageAlt,
  url,
  type = 'website',
  robots = 'index, follow',
  product = null,
  breadcrumbs = null,
  faq = null
}) => {
  const siteTitle = 'Fractallogy';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'Fractallogy delivers cutting-edge tech solutions: Optical CRM, Quick Commerce Grocery Apps, and AI-powered Recommendation Engines.';
  const defaultImage = 'https://fractallogy.com/images/fractallogy-og.jpg';
  const baseUrl = 'https://fractallogy.com';

  const seoDescription = description || defaultDescription;
  const seoImage = image || defaultImage;
  const seoImageAlt = imageAlt || fullTitle;
  const seoUrl = url ? `${baseUrl}${url}` : baseUrl;

  const productSchema = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: product.name,
        description: product.description,
        url: seoUrl,
        applicationCategory: product.applicationCategory || 'BusinessApplication',
        operatingSystem: product.operatingSystem || 'Web, iOS, Android',
        featureList: product.features,
        ...(product.downloadUrl && { downloadUrl: product.downloadUrl }),
        ...(product.screenshot && { screenshot: product.screenshot }),
        provider: {
          '@type': 'Organization',
          name: 'Fractallogy',
          url: baseUrl
        },
        offers: {
          '@type': 'Offer',
          price: product.price ?? '0',
          priceCurrency: product.priceCurrency || 'USD',
          availability: 'https://schema.org/InStock'
        }
      }
    : null;

  const breadcrumbSchema = breadcrumbs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`
        }))
      }
    : null;

  const faqSchema = faq?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      }
    : null;

  const webPageSchema = url
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: fullTitle,
        description: seoDescription,
        url: seoUrl,
        isPartOf: {
          '@type': 'WebSite',
          name: siteTitle,
          url: baseUrl
        },
        ...(product && {
          about: {
            '@type': 'SoftwareApplication',
            name: product.name
          }
        })
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={seoDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Fractallogy" />
      <meta name="robots" content={robots} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:alt" content={seoImageAlt} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={seoImageAlt} />

      <link rel="canonical" href={seoUrl} />

      {productSchema && (
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      )}
      {webPageSchema && (
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
